import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaTasks } from '@react-icons/all-files/fa/FaTasks';
import { FaShieldAlt } from '@react-icons/all-files/fa/FaShieldAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../commons/Breadcrumb';
import PageHeader from '../../../commons/PageHeader';
import PageForm, { FormField } from '../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../manager/sys-task.reducer';

const BACK = '/configuracion/seguridad/sys-tasks';

interface SysTaskForm {
  id: string;
  task: string;
  modulo: string;
}

const empty: SysTaskForm = { id: '', task: '', modulo: '' };

const fields: FormField<SysTaskForm>[] = [
  {
    name: 'id',
    label: 'ID',
    readOnly: true,
    editOnly: true,
  },
  {
    name: 'task',
    label: 'Tarea',
    required: true,
    placeholder: 'Descripción de la tarea o permiso',
  },
  {
    name: 'modulo',
    label: 'Módulo',
    required: true,
    placeholder: 'Módulo funcional (ej. Usuarios, WorkFlow)',
  },
];

export default function SysTasksFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.sysTask.entity) as any;
  const updating = useAppSelector(s => s.sysTask.updating);
  const updateSuccess = useAppSelector(s => s.sysTask.updateSuccess);

  const [form, setForm] = useState<SysTaskForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof SysTaskForm, string>>>({});

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        task: entity.task ?? '',
        modulo: entity.modulo ?? '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew ? 'Tarea de sistema creada correctamente.' : 'Tarea de sistema actualizada correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (name: keyof SysTaskForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof SysTaskForm, string>> = {};
    if (!form.task.trim()) errs.task = 'La tarea es requerida';
    if (!form.modulo.trim()) errs.modulo = 'El módulo es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const payload = {
      ...(isNew ? {} : { id: Number(form.id) }),
      task: form.task.trim(),
      modulo: form.modulo.trim(),
    };

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
          { label: 'Seguridad', to: '/configuracion/seguridad', icon: <FaShieldAlt /> },
          { label: 'Tareas de Sistema', to: BACK, icon: <FaTasks /> },
          { label: isNew ? 'Nueva tarea' : `Editar #${id}` },
        ]}
      />

      <PageHeader
        icon={<FaTasks />}
        iconColor="#0d9488"
        title={isNew ? 'Nueva Tarea de Sistema' : 'Editar Tarea de Sistema'}
        subtitle={isNew ? 'Define la tarea y el módulo al que aplica' : `Modificando tarea #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#0d9488"
      />
    </div>
  );
}
